import { useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { answerFileShare, useSharedFiles, type SharedFileItem } from '../../../lib/file-shares';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { Button, Card, TextButton, Typography } from '../ui';

// Files other people have shared with you: the offers waiting to be answered, and the ones you took.
//
// Two lists, kept apart all the way down from the service. A file you have not accepted must not sit
// among the files you have, or the accept step is decoration — and an offer that installed itself is
// not an offer.
//
// `error` is rendered as an error, never as "nothing". "Nobody has shared anything with you" and "we
// could not find out" are different answers, and this is the screen where the difference is
// invisible to the reader unless we make it visible.
export function SharedWithYou({ onOpen }: { onOpen?: (file: SharedFileItem) => void }) {
  const { t } = useTranslation();
  const state = useSharedFiles();
  const [answering, setAnswering] = useState<string | null>(null);
  const [answerFailed, setAnswerFailed] = useState(false);

  const answer = async (fileId: string, choice: 'accept' | 'decline') => {
    if (answering) return;
    setAnswering(fileId);
    setAnswerFailed(false);
    try {
      await answerFileShare(fileId, choice);
      // Re-read rather than patching locally: the list is the server's answer about access, and a
      // local edit would be this client's guess at it.
      await queryClient.invalidateQueries({ queryKey: queryKeys.sharedFiles() });
    } catch {
      setAnswerFailed(true);
    } finally {
      setAnswering(null);
    }
  };

  if (state.status === 'loading') {
    return null;
  }

  if (state.status === 'error') {
    return (
      <Card size="sm">
        <Typography variant="p2" color="secondary" component="p">
          {t('files.sharedWithMe.error')}
        </Typography>
      </Card>
    );
  }

  if (!state.files.length && !state.pending.length) {
    return null;
  }

  return (
    <div>
      <Typography variant="h4" color="strong" component="h3">
        {t('files.sharedWithMe.title')}
      </Typography>

      {/* Offers first: they are the only thing here that asks something of the reader. */}
      {state.pending.map((file) => (
        <Card key={file.fileId} variant="stone" size="sm">
          <Typography variant="p2" color="strong" component="p">
            {t('files.sharedWithMe.pendingBody', { name: file.sharedByName || file.ownerName || '', file: file.fileName })}
          </Typography>
          <div>
            <Button
              size="small"
              loading={answering === file.fileId}
              onClick={() => void answer(file.fileId, 'accept')}
            >
              {t('files.sharedWithMe.accept')}
            </Button>
            <TextButton
              size="small"
              disabled={answering === file.fileId}
              onClick={() => void answer(file.fileId, 'decline')}
            >
              {t('files.sharedWithMe.decline')}
            </TextButton>
          </div>
        </Card>
      ))}

      {answerFailed && (
        <Typography variant="p3" color="secondary" component="p">
          {t('files.sharedWithMe.answerFailed')}
        </Typography>
      )}

      {/* Accepted. Note these are NOT copies — each one is read from its owner's filesystem at the
          moment you open it, so you always see their current version and a revoke takes effect on
          your very next read. A file the owner has since moved or deleted is simply absent from this
          list: it is built from what can actually be opened, not from the grant rows. */}
      {state.files.map((file) => (
        <div key={file.fileId}>
          <TextButton size="small" onClick={() => onOpen?.(file)}>
            {file.fileName}
          </TextButton>
          {(file.ownerName || file.sharedByName) && (
            <Typography variant="p3" color="muted" component="span">
              {t('files.sharedWithMe.by', { name: file.sharedByName || file.ownerName })}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
}
